import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Send, Users, FileText, Mail, Loader2, Plus, Trash2, Pencil, Eye, X, Paperclip, RefreshCw, Download, AlertTriangle,
} from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { RichTextEditor } from '@/features/notifications/RichTextEditor';

/**
 * HR staff mailer — the merchant campaign flow (Notifications page), addressed to our own employees.
 * Groups are built from ACTIVE employees, filtered by branch, and an employee with no email on their
 * HR record falls back to their login address (the server resolves this; the UI just shows which).
 */

const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

type SubTab = 'send' | 'groups' | 'templates' | 'log';
const SUB_TABS = [
  { id: 'send' as const, label: 'Compose & Send', Icon: Send },
  { id: 'groups' as const, label: 'Employee Groups', Icon: Users },
  { id: 'templates' as const, label: 'Templates', Icon: FileText },
  { id: 'log' as const, label: 'Send Log', Icon: Mail },
];

type Group = {
  id: number; name: string; description?: string | null; branches: string[]; employeeIds: number[];
  isActive: boolean; createdByName?: string | null; createdAt: string;
  memberCount: number; emailableCount: number; staleCount: number;
};
type Member = {
  id: number; code: string; name: string; branch?: string | null; designation?: string | null;
  email?: string | null; emailSource?: 'EMPLOYEE' | 'LOGIN' | null;
};
type Attachment = { ref: string; fileName: string; mimetype?: string | null; sizeBytes?: number | null };
type Template = { id: number; name: string; subject: string; bodyHtml: string; attachments?: Attachment[] | null; groupId?: number | null };
type Campaign = {
  id: number; campaignNo: string; groupName?: string | null; subject: string; status: string;
  totalRecipients: number; sentCount: number; failedCount: number; skippedCount: number;
  errorMessage?: string | null; sentByName?: string | null; createdAt: string;
};

const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtBytes = (n?: number | null) => (!n ? '' : n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

const STATUS_BADGE: Record<string, string> = {
  QUEUED: 'bg-slate-100 text-slate-600', SENDING: 'bg-amber-100 text-amber-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700', FAILED: 'bg-rose-100 text-rose-700',
  SENT: 'bg-emerald-100 text-emerald-700', SKIPPED: 'bg-slate-100 text-slate-500',
};

function useGroups() {
  return useQuery<Group[]>({
    queryKey: ['hr-notif-groups'],
    queryFn: async () => (await api.get('/hr/notifications/groups', { params: { includeInactive: 'true' } })).data,
  });
}

export function HrNotificationsTab() {
  const [sub, setSub] = useState<SubTab>('send');
  const { data: stats } = useQuery<any>({
    queryKey: ['hr-notif-stats'],
    queryFn: async () => (await api.get('/hr/notifications/stats')).data,
    refetchInterval: 30000,
  });

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {[['Groups', stats?.groups], ['Templates', stats?.templates], ['Campaigns', stats?.campaigns], ['Emails Sent', stats?.emailsSent]].map(([l, v]) => (
          <div key={l as string} className="bg-white border border-slate-200 rounded-xl px-3 py-2.5">
            <div className="text-[11px] text-slate-500">{l as string}</div>
            <div className="text-lg font-bold text-slate-800">{(v as number) ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-4 overflow-x-auto">
        {SUB_TABS.map((t) => (
          <button key={t.id} onClick={() => setSub(t.id)}
            className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px whitespace-nowrap ${
              sub === t.id ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}>
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {sub === 'send' && <ComposeTab onSent={() => setSub('log')} />}
      {sub === 'groups' && <GroupsTab />}
      {sub === 'templates' && <TemplatesTab />}
      {sub === 'log' && <SendLogTab />}
    </div>
  );
}

/* ──────────────────────────────── Groups ──────────────────────────────── */

function GroupsTab() {
  const qc = useQueryClient();
  const { data: groups = [], isLoading } = useGroups();
  const [form, setForm] = useState<Group | 'new' | null>(null);
  const [members, setMembers] = useState<Group | null>(null);

  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/hr/notifications/groups/${id}`)).data,
    onSuccess: (r: any) => { toast.success(r?.message ?? 'Group deleted'); qc.invalidateQueries({ queryKey: ['hr-notif-groups'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not delete group'),
  });

  if (form) return <GroupForm group={form === 'new' ? null : form} onDone={() => { setForm(null); qc.invalidateQueries({ queryKey: ['hr-notif-groups'] }); }} onCancel={() => setForm(null)} />;

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-slate-500">Groups of employees you can send a circular to.</div>
        <button onClick={() => setForm('new')} className="px-3 py-2 bg-primary text-white rounded-lg text-sm flex items-center gap-1.5"><Plus size={15} /> New Group</button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2.5">Group</th>
              <th className="text-left px-3 py-2.5">Built from</th>
              <th className="text-left px-3 py-2.5">Employees</th>
              <th className="text-left px-3 py-2.5">Reachable</th>
              <th className="text-left px-3 py-2.5">Created</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="text-center py-10"><Loader2 size={18} className="mx-auto animate-spin text-primary" /></td></tr>}
            {!isLoading && !groups.length && <tr><td colSpan={6} className="text-center py-12 text-slate-400">No groups yet.</td></tr>}
            {groups.map((g) => (
              <tr key={g.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-800 flex items-center gap-2">
                    {g.name}
                    {!g.isActive && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">Inactive</span>}
                  </div>
                  {g.description && <div className="text-xs text-slate-500">{g.description}</div>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">{g.branches.length ? g.branches.map((b) => b.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())).join(', ') : 'Hand-picked'}</td>
                <td className="px-3 py-2">
                  <button onClick={() => setMembers(g)} className="text-primary hover:underline">{g.memberCount}</button>
                  {g.staleCount > 0 && <span className="ml-1 text-[10px] text-amber-600" title="Members whose employee record no longer exists">(+{g.staleCount} gone)</span>}
                </td>
                <td className={`px-3 py-2 ${g.emailableCount < g.memberCount ? 'text-amber-600' : 'text-slate-600'}`}>
                  {g.emailableCount}
                  {g.emailableCount < g.memberCount && <span className="text-[11px]"> of {g.memberCount}</span>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(g.createdAt)}<div className="text-slate-400">{g.createdByName}</div></td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => setMembers(g)} className="p-1.5 text-slate-500 hover:text-primary" title="View members"><Eye size={15} /></button>
                    <button onClick={() => setForm(g)} className="p-1.5 text-slate-500 hover:text-primary" title="Edit"><Pencil size={15} /></button>
                    <button onClick={() => { if (confirm(`Delete group "${g.name}"?`)) del.mutate(g.id); }} className="p-1.5 text-slate-500 hover:text-rose-600" title="Delete"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {members && <MembersModal group={members} onClose={() => setMembers(null)} />}
    </div>
  );
}

function MembersModal({ group, onClose }: { group: Group; onClose: () => void }) {
  const { data, isLoading } = useQuery<Group & { members: Member[] }>({
    queryKey: ['hr-notif-group', group.id],
    queryFn: async () => (await api.get(`/hr/notifications/groups/${group.id}`)).data,
  });
  return (
    <Modal isOpen onClose={onClose} title={group.name} subtitle={`${group.memberCount} employee(s)`} maxWidth="3xl">
      {isLoading ? <div className="py-10 text-center"><Loader2 size={18} className="mx-auto animate-spin text-primary" /></div> : (
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0">
              <tr><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Branch</th><th className="text-left px-3 py-2">Email</th></tr>
            </thead>
            <tbody>
              {(data?.members ?? []).map((m) => (
                <tr key={m.id} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-mono text-xs">{m.code}</td>
                  <td className="px-3 py-1.5">{m.name}<div className="text-[11px] text-slate-400">{m.designation}</div></td>
                  <td className="px-3 py-1.5 text-xs text-slate-600">{m.branch ?? '—'}</td>
                  <td className="px-3 py-1.5 text-xs">
                    {m.email ? (
                      <>
                        {m.email}
                        {m.emailSource === 'LOGIN' && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-sky-100 text-sky-700" title="No email on the HR record — using their login address">login</span>}
                      </>
                    ) : <span className="text-amber-600">No email — will be skipped</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function GroupForm({ group, onDone, onCancel }: { group: Group | null; onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [branches, setBranches] = useState<string[]>(group?.branches ?? []);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set(group?.employeeIds ?? []));
  const allRef = useRef<HTMLInputElement>(null);

  const { data: branchList = [] } = useQuery<{ key: string; label: string; count: number }[]>({
    queryKey: ['hr-notif-branches'],
    queryFn: async () => (await api.get('/hr/notifications/branches')).data,
  });
  const { data: employees = [], isLoading } = useQuery<Member[]>({
    queryKey: ['hr-notif-emp-options', branches],
    queryFn: async () => (await api.get('/hr/notifications/employee-options', { params: branches.length ? { branches: branches.join(',') } : {} })).data,
  });

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => [e.name, e.code, e.email, e.designation].some((v) => String(v ?? '').toLowerCase().includes(q)));
  }, [employees, search]);

  const allVisibleSelected = visible.length > 0 && visible.every((e) => selected.has(e.id));
  useEffect(() => {
    // React cannot set `indeterminate` from JSX — it is a DOM property, not an attribute.
    if (allRef.current) allRef.current.indeterminate = !allVisibleSelected && visible.some((e) => selected.has(e.id));
  }, [allVisibleSelected, visible, selected]);

  const save = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), description: description.trim() || null, branches, employeeIds: Array.from(selected) };
      return group ? (await api.patch(`/hr/notifications/groups/${group.id}`, body)).data
                   : (await api.post('/hr/notifications/groups', body)).data;
    },
    onSuccess: () => { toast.success(group ? 'Group updated' : 'Group created'); onDone(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save group'),
  });

  const noEmail = Array.from(selected).filter((id) => {
    const e = employees.find((x) => x.id === id);
    return e && !e.email;
  }).length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800">{group ? `Edit "${group.name}"` : 'New Employee Group'}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Group name *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Noida Office" className="w-full border rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Description</label>
          <input value={description ?? ''} onChange={(e) => setDescription(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" />
        </div>
      </div>

      <div className="mb-3">
        <label className="text-xs text-slate-500 block mb-1">Filter by branch (optional)</label>
        <div className="flex flex-wrap gap-1.5">
          {branchList.map((b) => {
            const on = branches.includes(b.key);
            return (
              <button key={b.key} type="button"
                onClick={() => setBranches(on ? branches.filter((x) => x !== b.key) : [...branches, b.key])}
                className={`px-2.5 py-1 rounded-full text-xs border ${on ? 'bg-primary text-white border-primary' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                {b.label} <span className="opacity-70">({b.count})</span>
              </button>
            );
          })}
          {branches.length > 0 && <button type="button" onClick={() => setBranches([])} className="text-xs text-slate-500 underline ml-1">Clear</button>}
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden mb-3">
        <div className="bg-slate-50 px-3 py-2 flex items-center gap-2 border-b border-slate-200">
          <input ref={allRef} type="checkbox" className="accent-primary" checked={allVisibleSelected}
            onChange={(e) => {
              const next = new Set(selected);
              // Operates on the VISIBLE rows only, so "select all" under a filter cannot quietly
              // sweep in employees the operator never saw.
              if (e.target.checked) visible.forEach((x) => next.add(x.id));
              else visible.forEach((x) => next.delete(x.id));
              setSelected(next);
            }} />
          <span className="text-xs text-slate-600">Select all shown ({visible.length})</span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / code / email"
            className="ml-auto border rounded px-2 py-1 text-xs w-56" />
        </div>
        <div className="max-h-[320px] overflow-y-auto">
          {isLoading && <div className="py-8 text-center"><Loader2 size={18} className="mx-auto animate-spin text-primary" /></div>}
          {!isLoading && !visible.length && <div className="py-8 text-center text-slate-400 text-sm">No active employees match.</div>}
          {visible.map((e) => (
            <label key={e.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer">
              <input type="checkbox" className="accent-primary" checked={selected.has(e.id)}
                onChange={(ev) => {
                  const next = new Set(selected);
                  if (ev.target.checked) next.add(e.id); else next.delete(e.id);
                  setSelected(next);
                }} />
              <span className="font-mono text-[11px] text-slate-400 w-24 shrink-0">{e.code}</span>
              <span className="text-sm text-slate-800 flex-1 truncate">{e.name}</span>
              <span className="text-[11px] text-slate-500 w-24 truncate">{e.branch ?? ''}</span>
              <span className="text-[11px] w-56 truncate">
                {e.email
                  ? <span className="text-slate-500">{e.email}{e.emailSource === 'LOGIN' && <span className="ml-1 text-sky-600">(login)</span>}</span>
                  : <span className="text-amber-600">No email on record</span>}
              </span>
            </label>
          ))}
        </div>
      </div>

      {noEmail > 0 && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3 flex items-start gap-1.5">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          {noEmail} selected employee(s) have no email on their HR record and no login account to fall back to — they'll be logged as skipped when you send.
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 border rounded text-sm">Cancel</button>
        <button onClick={() => save.mutate()} disabled={!name.trim() || !selected.size || save.isPending}
          className="px-4 py-2 bg-primary text-white rounded text-sm disabled:opacity-50">
          {save.isPending ? 'Saving…' : `Save group (${selected.size})`}
        </button>
      </div>
    </div>
  );
}

/* ──────────────────────────────── Attachments ──────────────────────────────── */

function AttachmentPicker({ value, onChange }: { value: Attachment[]; onChange: (v: Attachment[]) => void }) {
  const [busy, setBusy] = useState(false);
  const used = value.reduce((s, a) => s + (a.sizeBytes ?? 0), 0);
  const pct = Math.min(100, Math.round((used / MAX_ATTACHMENT_BYTES) * 100));

  const pick = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    let running = used;
    const added: Attachment[] = [];
    for (const f of Array.from(files)) {
      if (running + f.size > MAX_ATTACHMENT_BYTES) { toast.error(`"${f.name}" skipped — 20 MB total limit reached.`); continue; }
      try {
        const fd = new FormData();
        fd.append('file', f);
        const r = await api.post('/hr/notifications/attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        added.push({ ref: r.data.ref, fileName: r.data.fileName, mimetype: r.data.mimetype, sizeBytes: r.data.sizeBytes });
        running += Number(r.data.sizeBytes ?? f.size);
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? `Could not upload "${f.name}"`);
      }
    }
    if (added.length) onChange([...value, ...added]);
    setBusy(false);
  };

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm cursor-pointer hover:bg-slate-50 flex items-center gap-1.5">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />} Attach files
          <input type="file" multiple className="hidden" onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
        </label>
        {value.map((a, i) => (
          <span key={a.ref + i} className="text-xs bg-slate-100 rounded px-2 py-1 flex items-center gap-1.5">
            {a.fileName} <span className="text-slate-400">{fmtBytes(a.sizeBytes)}</span>
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-rose-500 hover:text-rose-700"><X size={12} /></button>
          </span>
        ))}
      </div>
      {used > 0 && (
        <div className="mt-1.5">
          <div className="h-1 bg-slate-100 rounded overflow-hidden"><div className={`h-full ${pct > 85 ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${pct}%` }} /></div>
          <div className="text-[11px] text-slate-400 mt-0.5">{fmtBytes(used)} of 20 MB</div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────── Compose ──────────────────────────────── */

function ComposeTab({ onSent }: { onSent: () => void }) {
  const qc = useQueryClient();
  const { data: groups = [] } = useGroups();
  const { data: templates = [] } = useQuery<Template[]>({
    queryKey: ['hr-notif-templates'],
    queryFn: async () => (await api.get('/hr/notifications/templates')).data,
  });

  const [groupId, setGroupId] = useState<number | ''>('');
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [preview, setPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const group = groups.find((g) => g.id === Number(groupId));
  const activeGroups = groups.filter((g) => g.isActive);

  const applyTemplate = (id: number | '') => {
    setTemplateId(id);
    if (!id) return;
    const t = templates.find((x) => x.id === Number(id));
    if (!t) return;
    setSubject(t.subject);
    setBodyHtml(t.bodyHtml);
    setAttachments(t.attachments ?? []);
    if (t.groupId) setGroupId(t.groupId);
  };

  const send = useMutation({
    mutationFn: async () => (await api.post('/hr/notifications/campaigns', {
      groupId: Number(groupId), templateId: templateId ? Number(templateId) : null, subject: subject.trim(), bodyHtml, attachments,
    })).data,
    onSuccess: (r: any) => {
      toast.success(r?.message ?? 'Campaign queued');
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ['hr-notif-campaigns'] });
      qc.invalidateQueries({ queryKey: ['hr-notif-stats'] });
      onSent();
    },
    onError: (e: any) => { setConfirming(false); toast.error(e?.response?.data?.message ?? 'Could not send'); },
  });

  const saveTemplate = useMutation({
    mutationFn: async (name: string) => (await api.post('/hr/notifications/templates', {
      name, subject: subject.trim(), bodyHtml, attachments, groupId: groupId ? Number(groupId) : null,
    })).data,
    onSuccess: () => { toast.success('Saved as template'); qc.invalidateQueries({ queryKey: ['hr-notif-templates'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save template'),
  });

  const canSend = !!groupId && !!subject.trim() && !!bodyHtml.trim() && (group?.emailableCount ?? 0) > 0;

  return (
    <div className="grid lg:grid-cols-[1fr_300px] gap-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Send to group *</label>
            <select value={groupId} onChange={(e) => setGroupId(e.target.value ? Number(e.target.value) : '')} className="w-full border rounded px-2 py-1.5 text-sm">
              <option value="">Select a group</option>
              {activeGroups.map((g) => <option key={g.id} value={g.id}>{g.name} — {g.emailableCount} employee(s)</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Start from a template</label>
            <select value={templateId} onChange={(e) => applyTemplate(e.target.value ? Number(e.target.value) : '')} className="w-full border rounded px-2 py-1.5 text-sm">
              <option value="">None</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs text-slate-500 block mb-1">Subject *</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" placeholder="e.g. Holiday notice — Independence Day" />
        </div>

        <div>
          <label className="text-xs text-slate-500 block mb-1">Message *</label>
          <RichTextEditor value={bodyHtml} onChange={setBodyHtml} />
        </div>

        <AttachmentPicker value={attachments} onChange={setAttachments} />

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <button onClick={() => setPreview(true)} disabled={!bodyHtml.trim()} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50 flex items-center gap-1.5"><Eye size={14} /> Preview</button>
          <button
            onClick={() => {
              const name = window.prompt('Template name?');
              if (name?.trim()) saveTemplate.mutate(name.trim());
            }}
            disabled={!subject.trim() || !bodyHtml.trim()}
            className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Save as template</button>
          <button onClick={() => setConfirming(true)} disabled={!canSend || send.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50">
            <Send size={14} /> Send to {group?.emailableCount ?? 0} employee(s)
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 h-fit">
        <div className="text-xs uppercase text-slate-400 font-medium mb-2">Recipients</div>
        {group ? (
          <div className="space-y-1.5 text-sm">
            <div className="font-medium text-slate-800">{group.name}</div>
            <div className="text-slate-600">{group.memberCount} employee(s) in group</div>
            <div className="text-emerald-700">{group.emailableCount} will receive it</div>
            {group.memberCount > group.emailableCount && (
              <div className="text-amber-600">{group.memberCount - group.emailableCount} skipped — no email address</div>
            )}
            {group.branches.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                {group.branches.map((b) => <span key={b} className="text-[11px] bg-slate-100 rounded px-1.5 py-0.5">{b.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}</span>)}
              </div>
            )}
          </div>
        ) : <div className="text-sm text-slate-400">Pick a group to see who receives this.</div>}
      </div>

      {preview && (
        <Modal isOpen onClose={() => setPreview(false)} title="Preview" subtitle={subject} maxWidth="3xl">
          <div className="prose-email max-h-[65vh] overflow-y-auto" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
        </Modal>
      )}

      {confirming && (
        <Modal isOpen onClose={() => setConfirming(false)} title="Send this email?" maxWidth="md">
          <div className="text-sm text-slate-700 space-y-2">
            <p>This will email <b>{group?.emailableCount}</b> employee(s) in <b>{group?.name}</b>.</p>
            <p className="text-slate-500">Subject: {subject}</p>
            {attachments.length > 0 && <p className="text-slate-500">{attachments.length} attachment(s), {fmtBytes(attachments.reduce((s, a) => s + (a.sizeBytes ?? 0), 0))}</p>}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setConfirming(false)} className="px-4 py-2 border rounded text-sm">Cancel</button>
            <button onClick={() => send.mutate()} disabled={send.isPending} className="px-4 py-2 bg-primary text-white rounded text-sm disabled:opacity-50">
              {send.isPending ? 'Sending…' : 'Send now'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ──────────────────────────────── Templates ──────────────────────────────── */

function TemplatesTab() {
  const qc = useQueryClient();
  const { data: templates = [], isLoading } = useQuery<Template[]>({
    queryKey: ['hr-notif-templates'],
    queryFn: async () => (await api.get('/hr/notifications/templates')).data,
  });
  const [edit, setEdit] = useState<Template | 'new' | null>(null);

  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/hr/notifications/templates/${id}`)).data,
    onSuccess: () => { toast.success('Template deleted'); qc.invalidateQueries({ queryKey: ['hr-notif-templates'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not delete'),
  });

  if (edit) return <TemplateForm template={edit === 'new' ? null : edit} onDone={() => { setEdit(null); qc.invalidateQueries({ queryKey: ['hr-notif-templates'] }); }} onCancel={() => setEdit(null)} />;

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-slate-500">Reusable circulars.</div>
        <button onClick={() => setEdit('new')} className="px-3 py-2 bg-primary text-white rounded-lg text-sm flex items-center gap-1.5"><Plus size={15} /> New Template</button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="text-left px-3 py-2.5">Name</th><th className="text-left px-3 py-2.5">Subject</th><th className="text-left px-3 py-2.5">Attachments</th><th className="px-3 py-2.5"></th></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={4} className="text-center py-10"><Loader2 size={18} className="mx-auto animate-spin text-primary" /></td></tr>}
            {!isLoading && !templates.length && <tr><td colSpan={4} className="text-center py-12 text-slate-400">No templates yet.</td></tr>}
            {templates.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-3 py-2 font-medium text-slate-800">{t.name}</td>
                <td className="px-3 py-2 text-slate-600">{t.subject}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{t.attachments?.length ? `${t.attachments.length} file(s)` : '—'}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => setEdit(t)} className="p-1.5 text-slate-500 hover:text-primary"><Pencil size={15} /></button>
                    <button onClick={() => { if (confirm(`Delete template "${t.name}"?`)) del.mutate(t.id); }} className="p-1.5 text-slate-500 hover:text-rose-600"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TemplateForm({ template, onDone, onCancel }: { template: Template | null; onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [bodyHtml, setBodyHtml] = useState(template?.bodyHtml ?? '');
  const [attachments, setAttachments] = useState<Attachment[]>(template?.attachments ?? []);

  const save = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), subject: subject.trim(), bodyHtml, attachments };
      return template ? (await api.patch(`/hr/notifications/templates/${template.id}`, body)).data
                      : (await api.post('/hr/notifications/templates', body)).data;
    },
    onSuccess: () => { toast.success(template ? 'Template updated' : 'Template created'); onDone(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save template'),
  });

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">{template ? `Edit "${template.name}"` : 'New Template'}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Template name *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Subject *</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" />
        </div>
      </div>
      <div>
        <label className="text-xs text-slate-500 block mb-1">Message *</label>
        <RichTextEditor value={bodyHtml} onChange={setBodyHtml} />
      </div>
      <AttachmentPicker value={attachments} onChange={setAttachments} />
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 border rounded text-sm">Cancel</button>
        <button onClick={() => save.mutate()} disabled={!name.trim() || !subject.trim() || !bodyHtml.trim() || save.isPending}
          className="px-4 py-2 bg-primary text-white rounded text-sm disabled:opacity-50">{save.isPending ? 'Saving…' : 'Save template'}</button>
      </div>
    </div>
  );
}

/* ──────────────────────────────── Send log ──────────────────────────────── */

function SendLogTab() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<Campaign | null>(null);

  const { data, isLoading } = useQuery<{ items: Campaign[]; total: number; pageSize: number }>({
    queryKey: ['hr-notif-campaigns', { search, status, page }],
    queryFn: async () => (await api.get('/hr/notifications/campaigns', { params: { search: search || undefined, status: status || undefined, page, pageSize: 25 } })).data,
    // Poll only while something is actually in flight, so an idle log isn't hitting the API forever.
    refetchInterval: (q: any) => (q.state.data?.items ?? []).some((c: Campaign) => c.status === 'QUEUED' || c.status === 'SENDING') ? 5000 : false,
  });

  const items = data?.items ?? [];
  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / (data?.pageSize ?? 25)));

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search campaign no / subject / group" className="border rounded-lg px-3 py-2 text-sm flex-1 min-w-[220px]" />
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="border rounded-lg px-2 py-2 text-sm">
          <option value="">All statuses</option>
          {['QUEUED', 'SENDING', 'COMPLETED', 'FAILED'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2.5">Campaign</th><th className="text-left px-3 py-2.5">Group</th>
              <th className="text-left px-3 py-2.5">Subject</th><th className="text-left px-3 py-2.5">Progress</th>
              <th className="text-left px-3 py-2.5">Status</th><th className="text-left px-3 py-2.5">Sent by</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="text-center py-10"><Loader2 size={18} className="mx-auto animate-spin text-primary" /></td></tr>}
            {!isLoading && !items.length && <tr><td colSpan={6} className="text-center py-12 text-slate-400">Nothing sent yet.</td></tr>}
            {items.map((c) => {
              const done = c.sentCount + c.failedCount + c.skippedCount;
              const pct = c.totalRecipients ? Math.round((done / c.totalRecipients) * 100) : 0;
              return (
                <tr key={c.id} onClick={() => setOpen(c)} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer">
                  <td className="px-3 py-2 font-mono text-xs text-primary">{c.campaignNo}<div className="text-slate-400">{fmtDate(c.createdAt)}</div></td>
                  <td className="px-3 py-2 text-xs text-slate-600">{c.groupName ?? '—'}</td>
                  <td className="px-3 py-2"><div className="max-w-[280px] truncate">{c.subject}</div></td>
                  <td className="px-3 py-2 w-40">
                    <div className="h-1.5 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {c.sentCount} sent{c.failedCount ? ` · ${c.failedCount} failed` : ''}{c.skippedCount ? ` · ${c.skippedCount} skipped` : ''} / {c.totalRecipients}
                    </div>
                  </td>
                  <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_BADGE[c.status] ?? ''}`}>{c.status}</span></td>
                  <td className="px-3 py-2 text-xs text-slate-500">{c.sentByName ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-sm">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 border rounded disabled:opacity-40">Prev</button>
          <span className="text-slate-500">Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 border rounded disabled:opacity-40">Next</button>
        </div>
      )}
      {open && <CampaignLogModal campaign={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function CampaignLogModal({ campaign, onClose }: { campaign: Campaign; onClose: () => void }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const { data, isLoading } = useQuery<any>({
    queryKey: ['hr-notif-campaign', campaign.id, status],
    queryFn: async () => (await api.get(`/hr/notifications/campaigns/${campaign.id}`, { params: { status: status || undefined } })).data,
    refetchInterval: (q: any) => (q.state.data?.status === 'QUEUED' || q.state.data?.status === 'SENDING') ? 4000 : false,
  });

  const retry = useMutation({
    mutationFn: async () => (await api.post(`/hr/notifications/campaigns/${campaign.id}/retry`)).data,
    onSuccess: (r: any) => { toast.success(r?.message ?? 'Retrying'); qc.invalidateQueries({ queryKey: ['hr-notif-campaign', campaign.id] }); qc.invalidateQueries({ queryKey: ['hr-notif-campaigns'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not retry'),
  });

  return (
    <Modal isOpen onClose={onClose} title={campaign.campaignNo} subtitle={campaign.subject} maxWidth="4xl">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded px-2 py-1.5 text-sm">
          <option value="">All recipients</option>
          {['SENT', 'FAILED', 'SKIPPED', 'QUEUED'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {(data?.failedCount ?? 0) > 0 && (
          <button onClick={() => retry.mutate()} disabled={retry.isPending} className="px-3 py-1.5 border border-primary text-primary rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50">
            <RefreshCw size={14} /> Retry {data.failedCount} failed
          </button>
        )}
        <button onClick={() => downloadBlob(`/hr/notifications/campaigns/${campaign.id}/export`, `hr-campaign-${campaign.campaignNo}.csv`)}
          className="px-3 py-1.5 border rounded-lg text-sm flex items-center gap-1.5 ml-auto"><Download size={14} /> Export CSV</button>
      </div>
      {data?.errorMessage && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2">{data.errorMessage}</div>}
      {isLoading ? <div className="py-10 text-center"><Loader2 size={18} className="mx-auto animate-spin text-primary" /></div> : (
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0">
              <tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Email</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Detail</th></tr>
            </thead>
            <tbody>
              {(data?.logs ?? []).map((l: any) => (
                <tr key={l.id} className="border-t border-slate-100">
                  <td className="px-3 py-1.5"><span className="font-mono text-[11px] text-slate-400 mr-1.5">{l.employeeCode}</span>{l.employeeName}</td>
                  <td className="px-3 py-1.5 text-xs">
                    {l.email ?? <span className="text-slate-400">—</span>}
                    {l.emailSource === 'LOGIN' && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-sky-100 text-sky-700" title="Taken from their login account">login</span>}
                  </td>
                  <td className="px-3 py-1.5"><span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_BADGE[l.status] ?? ''}`}>{l.status}</span></td>
                  <td className="px-3 py-1.5 text-xs text-slate-500">{l.error ?? (l.sentAt ? fmtDate(l.sentAt) : '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
